import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Calendar,
	CheckCircle,
	Filter,
	Image,
	List,
	Plus,
	Users,
	XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/attendance-tracking",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 sm:space-y-8">
			{/* Mobile Back Button */}
			<Link
				to="/app/help/user-guide"
				className="md:hidden flex items-center gap-2 text-primary"
			>
				<ArrowLeft className="w-4 h-4" />
				{t("common.back")}
			</Link>

			{/* Header */}
			<div className="mb-6 sm:mb-8">
				<div className="flex items-center space-x-3 mb-4">
					<div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
						<Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
					</div>
					<div className="min-w-0">
						<h1 className="text-xl sm:text-3xl font-bold text-base-content">
							{t("help.attendanceTracking")}
						</h1>
						<p className="text-sm sm:text-lg text-base-content/70">
							{t("help.attendanceTrackingDescription")}
						</p>
					</div>
				</div>
			</div>

			{/* Overview */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Calendar className="h-5 w-5" />
						<span>{t("help.attendanceOverview")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.attendanceOverviewDescription")}
					</p>
					<div className="bg-info/10 border border-info/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-info mt-0.5" />
							<div>
								<h4 className="font-medium text-info">
									{t("help.attendanceKeyFeatures")}
								</h4>
								<ul className="text-sm text-info/80 mt-2 space-y-1">
									<li>• {t("help.attendanceKeyFeature1")}</li>
									<li>• {t("help.attendanceKeyFeature2")}</li>
									<li>• {t("help.attendanceKeyFeature3")}</li>
									<li>• {t("help.attendanceKeyFeature4")}</li>
									<li>• {t("help.attendanceKeyFeature5")}</li>
								</ul>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Accessing Attendance Tracking */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Calendar className="h-5 w-5" />
						<span>{t("help.attendanceAccessing")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.attendanceAccessingDescription")}
					</p>
					<ol className="list-decimal list-inside space-y-2 text-base-content/80">
						<li>{t("help.attendanceAccessingStep1")}</li>
						<li>{t("help.attendanceAccessingStep2")}</li>
						<li>{t("help.attendanceAccessingStep3")}</li>
						<li>{t("help.attendanceAccessingStep4")}</li>
					</ol>
					<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
						<div className="flex items-start space-x-3">
							<AlertCircle className="h-5 w-5 text-warning mt-0.5" />
							<div>
								<h4 className="font-medium text-warning">
									{t("help.attendancePermissionRequirements")}
								</h4>
								<p className="text-sm text-warning/80 mt-1">
									{t("help.attendancePermissionDescription")}
								</p>
							</div>
						</div>
					</div>
					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.attendanceImagePlaceholder1")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* View Modes */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Calendar className="h-5 w-5" />
						<span>{t("help.attendanceViewModes")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.attendanceViewModesDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceCalendarView")}
							</h4>
							<div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
								<h5 className="font-medium text-primary mb-2 flex items-center">
									<Calendar className="h-4 w-4 mr-2" />
									{t("help.attendanceCalendarViewTitle")}
								</h5>
								<ul className="text-sm text-primary/80 space-y-1">
									<li>• {t("help.attendanceCalendarFeature1")}</li>
									<li>• {t("help.attendanceCalendarFeature2")}</li>
									<li>• {t("help.attendanceCalendarFeature3")}</li>
									<li>• {t("help.attendanceCalendarFeature4")}</li>
									<li>• {t("help.attendanceCalendarFeature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceListView")}
							</h4>
							<div className="bg-success/10 border border-success/20 rounded-lg p-4">
								<h5 className="font-medium text-success mb-2 flex items-center">
									<List className="h-4 w-4 mr-2" />
									{t("help.attendanceListViewTitle")}
								</h5>
								<ul className="text-sm text-success/80 space-y-1">
									<li>• {t("help.attendanceListFeature1")}</li>
									<li>• {t("help.attendanceListFeature2")}</li>
									<li>• {t("help.attendanceListFeature3")}</li>
									<li>• {t("help.attendanceListFeature4")}</li>
									<li>• {t("help.attendanceListFeature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceViewModeSelection")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.attendanceViewModeFeature1")}</li>
								<li>• {t("help.attendanceViewModeFeature2")}</li>
								<li>• {t("help.attendanceViewModeFeature3")}</li>
								<li>• {t("help.attendanceViewModeFeature4")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.attendanceImagePlaceholder2")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Filtering and Search */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Filter className="h-5 w-5" />
						<span>{t("help.attendanceFiltering")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.attendanceFilteringDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceInstructorFilter")}
							</h4>
							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<h5 className="font-medium text-info mb-2">
									{t("help.attendanceInstructorFilterTitle")}
								</h5>
								<ul className="text-sm text-info/80 space-y-1">
									<li>• {t("help.attendanceInstructorFeature1")}</li>
									<li>• {t("help.attendanceInstructorFeature2")}</li>
									<li>• {t("help.attendanceInstructorFeature3")}</li>
									<li>• {t("help.attendanceInstructorFeature4")}</li>
									<li>• {t("help.attendanceInstructorFeature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceAdvancedFilters")}
							</h4>
							<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
								<h5 className="font-medium text-warning mb-2">
									{t("help.attendanceAdvancedFiltersTitle")}
								</h5>
								<ul className="text-sm text-warning/80 space-y-1">
									<li>• {t("help.attendanceAdvancedFeature1")}</li>
									<li>• {t("help.attendanceAdvancedFeature2")}</li>
									<li>• {t("help.attendanceAdvancedFeature3")}</li>
									<li>• {t("help.attendanceAdvancedFeature4")}</li>
									<li>• {t("help.attendanceAdvancedFeature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceFilterManagement")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.attendanceFilterFeature1")}</li>
								<li>• {t("help.attendanceFilterFeature2")}</li>
								<li>• {t("help.attendanceFilterFeature3")}</li>
								<li>• {t("help.attendanceFilterFeature4")}</li>
							</ul>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.attendanceImagePlaceholder3")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Session Details and Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Users className="h-5 w-5" />
						<span>{t("help.attendanceSessionDetails")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.attendanceSessionDetailsDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceSessionInformation")}
							</h4>
							<div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
								<h5 className="font-medium text-primary mb-2 flex items-center">
									<Calendar className="h-4 w-4 mr-2" />
									{t("help.attendanceSessionInformationTitle")}
								</h5>
								<ul className="text-sm text-primary/80 space-y-1">
									<li>• {t("help.attendanceSessionInfoFeature1")}</li>
									<li>• {t("help.attendanceSessionInfoFeature2")}</li>
									<li>• {t("help.attendanceSessionInfoFeature3")}</li>
									<li>• {t("help.attendanceSessionInfoFeature4")}</li>
									<li>• {t("help.attendanceSessionInfoFeature5")}</li>
									<li>• {t("help.attendanceSessionInfoFeature6")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceListManagement")}
							</h4>
							<div className="bg-success/10 border border-success/20 rounded-lg p-4">
								<h5 className="font-medium text-success mb-2 flex items-center">
									<Users className="h-4 w-4 mr-2" />
									{t("help.attendanceListManagementTitle")}
								</h5>
								<ul className="text-sm text-success/80 space-y-1">
									<li>• {t("help.attendanceListManagementFeature1")}</li>
									<li>• {t("help.attendanceListManagementFeature2")}</li>
									<li>• {t("help.attendanceListManagementFeature3")}</li>
									<li>• {t("help.attendanceListManagementFeature4")}</li>
									<li>• {t("help.attendanceListManagementFeature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceAddingCustomers")}
							</h4>
							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<h5 className="font-medium text-info mb-2 flex items-center">
									<Plus className="h-4 w-4 mr-2" />
									{t("help.attendanceAddingCustomersTitle")}
								</h5>
								<ul className="text-sm text-info/80 space-y-1">
									<li>• {t("help.attendanceAddingFeature1")}</li>
									<li>• {t("help.attendanceAddingFeature2")}</li>
									<li>• {t("help.attendanceAddingFeature3")}</li>
									<li>• {t("help.attendanceAddingFeature4")}</li>
									<li>• {t("help.attendanceAddingFeature5")}</li>
								</ul>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceStatusIndicators")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.attendanceCapacityStatus")}
										</h5>
										<ul className="text-sm text-base-content/70 space-y-1">
											<li>• {t("help.attendanceCapacityAvailable")}</li>
											<li>• {t("help.attendanceCapacityAlmostFull")}</li>
											<li>• {t("help.attendanceCapacityFull")}</li>
										</ul>
									</div>
								</div>
								<div className="space-y-2">
									<div>
										<h5 className="font-medium text-sm text-base-content">
											{t("help.attendanceSessionStatus")}
										</h5>
										<ul className="text-sm text-base-content/70 space-y-1">
											<li>• {t("help.attendanceSessionActive")}</li>
											<li>• {t("help.attendanceSessionCanceled")}</li>
											<li>• {t("help.attendanceSessionInactive")}</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.attendanceImagePlaceholder4")}</em>
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Attendance Status Management */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<CheckCircle className="h-5 w-5" />
						<span>{t("help.attendanceStatusManagement")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-base-content/80">
						{t("help.attendanceStatusManagementDescription")}
					</p>

					<div className="space-y-4">
						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceAvailableStatuses")}
							</h4>
							<div className="grid md:grid-cols-2 gap-4">
								<div className="space-y-3">
									<div className="bg-success/10 border border-success/20 rounded-lg p-3">
										<h5 className="font-medium text-success flex items-center mb-2">
											<CheckCircle className="h-4 w-4 mr-2" />
											{t("help.attendanceStatusConfirmed")}
										</h5>
										<p className="text-sm text-success/80">
											{t("help.attendanceStatusConfirmedDescription")}
										</p>
									</div>
									<div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
										<h5 className="font-medium text-warning flex items-center mb-2">
											<AlertCircle className="h-4 w-4 mr-2" />
											{t("help.attendanceStatusTrial")}
										</h5>
										<p className="text-sm text-warning/80">
											{t("help.attendanceStatusTrialDescription")}
										</p>
									</div>
								</div>
								<div className="space-y-3">
									<div className="bg-info/10 border border-info/20 rounded-lg p-3">
										<h5 className="font-medium text-info flex items-center mb-2">
											<AlertCircle className="h-4 w-4 mr-2" />
											{t("help.attendanceStatusReposition")}
										</h5>
										<p className="text-sm text-info/80">
											{t("help.attendanceStatusRepositionDescription")}
										</p>
									</div>
									<div className="bg-error/10 border border-error/20 rounded-lg p-3">
										<h5 className="font-medium text-error flex items-center mb-2">
											<XCircle className="h-4 w-4 mr-2" />
											{t("help.attendanceStatusCanceled")}
										</h5>
										<p className="text-sm text-error/80">
											{t("help.attendanceStatusCanceledDescription")}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceStatusChangeProcess")}
							</h4>
							<ol className="list-decimal list-inside space-y-1 text-sm text-base-content/70">
								<li>{t("help.attendanceStatusChangeStep1")}</li>
								<li>{t("help.attendanceStatusChangeStep2")}</li>
								<li>{t("help.attendanceStatusChangeStep3")}</li>
								<li>{t("help.attendanceStatusChangeStep4")}</li>
								<li>{t("help.attendanceStatusChangeStep5")}</li>
							</ol>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceStatusChangeImplications")}
							</h4>
							<ul className="text-sm text-base-content/70 space-y-1">
								<li>• {t("help.attendanceStatusImplication1")}</li>
								<li>• {t("help.attendanceStatusImplication2")}</li>
								<li>• {t("help.attendanceStatusImplication3")}</li>
								<li>• {t("help.attendanceStatusImplication4")}</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium text-base-content mb-2">
								{t("help.attendanceBulkStatusManagement")}
							</h4>
							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<h5 className="font-medium text-info mb-2">
									{t("help.attendanceBulkStatusTitle")}
								</h5>
								<ul className="text-sm text-info/80 space-y-1">
									<li>• {t("help.attendanceBulkFeature1")}</li>
									<li>• {t("help.attendanceBulkFeature2")}</li>
									<li>• {t("help.attendanceBulkFeature3")}</li>
									<li>• {t("help.attendanceBulkFeature4")}</li>
								</ul>
							</div>
						</div>
					</div>

					<div className="bg-base-200 rounded-lg p-4">
						<p className="text-sm text-base-content/70">
							<Image className="h-4 w-4 inline mr-2" />
							<em>{t("help.attendanceImagePlaceholder5")}</em>
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
